import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { listProductsGroupWise } from "../../Redux/Actions/ProductActions";
import Loading from "../LoadingError/Loading";
import Message from "../LoadingError/Error";
import {
  DEFAULT_CURRENCY,
  IMAGE_CACHE_URL,
} from "../../Redux/Constants/GlobalConstant";
import QuantityInput from "../QuantityInput";

const SCROLL_SPEED = 0.6; // pixels per frame

const CustomThumbSection = (props) => {
  const dispatch = useDispatch();
  const { groupId } = props;
  const [products, setProducts] = useState(null);
  const [heading, setHeading] = useState(null);

  const groupListDetail = useSelector((state) => state.groupProductList);
  const { loading, error, groupProducts } = groupListDetail;

  const cart = useSelector((state) => state.cart);
  const { cartItems = [] } = cart;

  // Auto-scroll hooks
  const scrollRef = useRef(null);
  const animRef = useRef(null);
  const posRef = useRef(0);

  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  useEffect(() => {
    if (!groupProducts) dispatch(listProductsGroupWise(groupId));
    if (groupProducts && !products) {
      setProducts(groupProducts.products);
      setHeading(groupProducts?.group_name);
    }
  }, [dispatch, groupId, groupProducts, products]);

  // Auto-scroll loop
  const tick = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      if (dragging.current) {
        // skip — drag is controlling scrollLeft directly
      } else if (hoveringRef.current) {
        posRef.current += hoverSpeed.current;
        const max = el.scrollWidth - el.clientWidth;
        posRef.current = Math.max(0, Math.min(max, posRef.current));
        el.scrollLeft = posRef.current;
      } else {
        // default auto-scroll right
        const halfWidth = el.scrollWidth / 2;
        posRef.current += SCROLL_SPEED;
        if (posRef.current >= halfWidth) posRef.current = 0;
        el.scrollLeft = posRef.current;
      }
      setShowLeft(posRef.current > 10);
      setShowRight(true);
    }
    animRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (!loading && !error && products && products.length > 0) {
      animRef.current = requestAnimationFrame(tick);
    }
    return () => cancelAnimationFrame(animRef.current);
  }, [loading, error, products, tick]);

  const slide = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = dir === "left" ? -220 : 220;
    posRef.current = Math.max(0, posRef.current + amount);
    el.scrollLeft = posRef.current;
  };

  const dragging    = useRef(false);
  const startX      = useRef(0);
  const startScroll = useRef(0);
  const didDrag     = useRef(false);

  // Hover-scroll (desktop)
  const hoveringRef = useRef(false);
  const hoverSpeed  = useRef(0);

  const onMouseMove = (e) => {
    const el = scrollRef.current;
    if (!el || dragging.current) return;
    const { left, width } = el.getBoundingClientRect();
    const centered = (e.clientX - left) / width - 0.5;
    hoverSpeed.current = Math.abs(centered) < 0.15 ? 0 : Math.sign(centered) * ((Math.abs(centered) - 0.15) / 0.35) * 5;
  };
  const onMouseEnter = () => { hoveringRef.current = true; };
  const onMouseLeave = () => { hoveringRef.current = false; hoverSpeed.current = 0; };

  // Mouse drag (desktop)
  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragging.current    = true;
    didDrag.current     = false;
    startX.current      = e.clientX;
    startScroll.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = "grabbing";
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current || !scrollRef.current) return;
      const dx = e.clientX - startX.current;
      if (Math.abs(dx) > 4) didDrag.current = true;
      const next = startScroll.current - dx;
      scrollRef.current.scrollLeft = next;
      posRef.current = next;
    };
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      if (scrollRef.current) scrollRef.current.style.cursor = "default";
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, []);

  // Touch drag (mobile)
  const onTouchStart = (e) => {
    hoveringRef.current = false;
    dragging.current    = true;
    didDrag.current     = false;
    startX.current      = e.touches[0].clientX;
    startScroll.current = scrollRef.current.scrollLeft;
  };
  const onTouchMove = (e) => {
    if (!dragging.current || !scrollRef.current) return;
    const dx = e.touches[0].clientX - startX.current;
    if (Math.abs(dx) > 4) didDrag.current = true;
    const next = startScroll.current - dx;
    scrollRef.current.scrollLeft = next;
    posRef.current = next;
  };
  const onTouchEnd = () => { dragging.current = false; };

  const guardClick = (e) => { if (didDrag.current) e.preventDefault(); };

  return (
    <>
      <div className="container">
        <div className="section">
          <div className="row mobile-thumbnail">
            <div className="col-lg-12 col-md-12 article">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h5>{heading}</h5>
                <Link to={`/group/${groupId}`} className="view-all-btn" style={{ position: "relative", zIndex: 10 }}>
                  View All
                </Link>
              </div>
              
              <div className="bd-scroll-wrapper">
                {/* ── Left Arrow ── */}
                <button
                  className={`bd-nav-btn bd-nav-btn--left ${showLeft ? "bd-nav-btn--visible" : ""}`}
                  onClick={() => slide("left")}
                  aria-label="Scroll left"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>

                <div 
                  className="best-deal-shopcontainer row bd-track"
                  ref={scrollRef}
                  onMouseMove={onMouseMove}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  onMouseDown={onMouseDown}
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                  style={{ userSelect: "none" }}
                >
                  {loading ? (
                    <div className="mb-5">
                      <Loading />
                    </div>
                  ) : error ? (
                    <Message variant="alert-danger">{error}</Message>
                  ) : (
                    <>
                      {/* Render products TWICE for seamless infinite loop */}
                      {products && [...products, ...products].map((product, i) => (
                        <div className="shop col-lg-2 col-md-3 col-sm-3 bd-card" key={i}>
                          <div className="border-product">
                            <Link to={`/products/${product._id}`} onClick={guardClick}>
                              <div className="shopBack">
                                {/* {product.mrp_price - product.price > 0 && (
                                  <div className="offer_strip">
                                    {Math.floor(
                                      ((product.mrp_price - product.price) /
                                        product.price) *
                                        100
                                    )}
                                    % off
                                  </div>
                                )} */}
                                <img
                                  src={IMAGE_CACHE_URL + product.image}
                                  alt={product.name}
                                />
                              </div>
                            </Link>
                            <div className="shoptext">
                              <p>
                                <Link to={`/products/${product._id}`}>
                                  {product.name} - {product?.unit}
                                </Link>
                              </p>
                              <div className="price-button-thumb-sec">
                                <h3>
                                  {product.mrp_price - product.price > 0 && (
                                    <span className="text-overline">
                                      {DEFAULT_CURRENCY} {product.mrp_price}
                                    </span>
                                  )}
                                  <span>
                                    {DEFAULT_CURRENCY} {product.price}
                                  </span>
                                </h3>
                                <QuantityInput
                                  productId={product._id}
                                  renderAddButton={true}
                                  qty={cartItems.find(x => x.product === product._id)?.qty ?? 0}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>

                {/* ── Right Arrow ── */}
                <button
                  className={`bd-nav-btn bd-nav-btn--right ${showRight ? "bd-nav-btn--visible" : ""}`}
                  onClick={() => slide("right")}
                  aria-label="Scroll right"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomThumbSection;
